import { readCreatorStorefrontFromStorage } from './creatorStorefront';
import {
  normalizeCreatorSlugInput,
  rememberCreatorSlug,
  resolveCreatorSlug,
  suggestCreatorSlugFromName,
} from './creatorSlug';

/** Slugify one segment for `/{creator}-{name}-{id}` product URLs. */
export function slugifyProductUrlPart(text) {
  if (!text || String(text).trim() === '') return '';
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Trailing id segment (productId, SKU sequence, or short Mongo suffix). */
export function getProductUrlIdSegment(product = {}) {
  const pid = product.productId ?? product.product_id;
  if (pid != null && String(pid).trim() !== '') {
    return String(pid)
      .trim()
      .toLowerCase()
      .replace(/_/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  const sku = product.sku;
  if (sku) {
    const parts = String(sku).split('-');
    const last = parts[parts.length - 1];
    if (/^\d{5}$/.test(last)) return last;
    return slugifyProductUrlPart(sku);
  }

  const mongoId = product._id ?? product.id;
  if (mongoId && /^[a-f0-9]{24}$/i.test(String(mongoId))) {
    return String(mongoId).slice(-8).toLowerCase();
  }

  return '';
}

/** Creator handle for URL prefix (shop slug, not brand). */
export function resolveCreatorSlugForProductUrl(product, currentUser = null) {
  if (currentUser) {
    const fromAdmin = resolveCreatorSlugForAdminUser(currentUser);
    if (fromAdmin) return fromAdmin;
  }

  const createdBy = product?.createdBy;
  if (createdBy) {
    if (typeof createdBy === 'object') {
      const fromObj = normalizeCreatorSlugInput(createdBy.creatorSlug);
      if (fromObj) return fromObj;
      const fromName = suggestCreatorSlugFromName(createdBy.name);
      if (fromName) return fromName;
    }
    const fromResolve = resolveCreatorSlug(createdBy);
    if (fromResolve) return fromResolve;
  }

  return '';
}

export function resolveCreatorSlugForAdminUser(currentUser) {
  if (!currentUser) return '';

  const direct = normalizeCreatorSlugInput(currentUser.creatorSlug);
  if (direct) return direct;

  const uid = currentUser._id || currentUser.id;
  if (uid) {
    const stored = readCreatorStorefrontFromStorage(uid);
    const fromStored = normalizeCreatorSlugInput(stored?.creatorSlug);
    if (fromStored) return fromStored;
    rememberAdminUserSlug(uid, currentUser);
  }

  const fromResolve = resolveCreatorSlug(currentUser);
  if (fromResolve) return fromResolve;

  return suggestCreatorSlugFromName(
    currentUser.name || String(currentUser.email || '').split('@')[0] || ''
  );
}

function rememberAdminUserSlug(uid, user) {
  const slug = normalizeCreatorSlugInput(user.creatorSlug);
  if (slug && uid) rememberCreatorSlug(uid, slug);
}

/**
 * Public product path segment: `{creatorSlug}-{product-name-slug}-{productId}`.
 * Example: `umardaraz-cotton-shirt-00050`.
 */
export function buildProductSeoSlug({
  name,
  creatorSlug,
  productId,
  sku,
  product,
  currentUser,
} = {}) {
  const row = product || {};
  const creator =
    normalizeCreatorSlugInput(creatorSlug) ||
    resolveCreatorSlugForProductUrl(row, currentUser) ||
    slugifyProductUrlPart(row.brand) ||
    'store';

  const namePart = slugifyProductUrlPart(name ?? row.name);
  const idPart = getProductUrlIdSegment({
    productId: productId ?? row.productId,
    sku: sku ?? row.sku,
    _id: row._id,
    id: row.id,
  });

  const parts = [creator, namePart, idPart].filter(Boolean);
  return parts.join('-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

/** Storefront path (`/slug` or legacy `/product/:id`). */
export function getProductStorefrontPath(product) {
  if (!product) return '/';

  const raw = product.url != null ? String(product.url).trim() : '';
  if (raw) {
    const slug = raw.replace(/^\//, '');
    return slug.startsWith('http') ? raw : `/${slug}`;
  }

  const built = buildProductSeoSlug({ product });
  if (built) return `/${built}`;

  const id = product.id ?? product._id;
  return id ? `/product/${id}` : '/';
}

/** Absolute share URL (path only helper — origin added by caller). */
export function getProductStorefrontSlug(product) {
  const path = getProductStorefrontPath(product);
  if (path.startsWith('http')) {
    try {
      return new URL(path).pathname.replace(/^\//, '');
    } catch {
      return '';
    }
  }
  return path.replace(/^\//, '');
}
